import React, { useEffect, useMemo, useState, type FunctionComponent } from "react";
import { NotificationOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tooltip,
  Typography,
} from "antd";

import { ChatType } from "@fitness-app/data-models/entities/Chat";
import {
  DEFAULT_ANNOUNCEMENT_CHANNEL_NAME,
  type AnnouncementChannelConfig,
} from "@fitness-app/data-models/entities/TrainerConfig";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import {
  useAnnouncementChannelOverview,
  useBackfillAnnouncementChannels,
  useUpdateAnnouncementChannelConfig,
  type AnnouncementChannelOverview,
} from "./useAnnouncementChannel";

interface OwnProps {
  open: boolean;
  onClose: () => void;
  /** CHAT_WRITE scope ALL — backfill actions render disabled without it. */
  canWrite: boolean;
}

type ConfigFormModel = {
  enabled: boolean;
  channelName: string;
  channelType: AnnouncementChannelConfig["channelType"];
  applyToExisting?: boolean;
};

type ClientWithoutChannel = AnnouncementChannelOverview["clientsWithoutChannel"][number];

const AnnouncementChannelModal: FunctionComponent<OwnProps> = ({ open, onClose, canWrite }) => {
  const { isOwner } = useUserClaims();
  const [formController] = Form.useForm<ConfigFormModel>();
  const [search, setSearch] = useState("");
  const [productFilter, setProductFilter] = useState<string | null>(null);
  const overview = useAnnouncementChannelOverview(open);
  const updateConfig = useUpdateAnnouncementChannelConfig();
  const backfill = useBackfillAnnouncementChannels();
  const [pendingTraineeId, setPendingTraineeId] = useState<string | null>(null);

  const config = overview.data?.config;
  const enabled = config?.enabled ?? false;
  const canBackfill = enabled && canWrite;
  const backfillDisabledHint = !enabled
    ? "Najpierw włącz i zapisz konfigurację kanału"
    : !canWrite
      ? "Wymaga uprawnienia do pisania na czacie w zakresie całego konta"
      : null;

  useEffect(() => {
    if (overview.data) {
      formController.setFieldsValue({
        enabled: overview.data.config?.enabled ?? false,
        channelName: overview.data.config?.channelName ?? "",
        channelType: overview.data.config?.channelType ?? ChatType.OneWayMarketing,
      });
    }
  }, [overview.data, formController]);

  const productOptions = useMemo(() => {
    const byId = new Map<string, string>();
    (overview.data?.clientsWithoutChannel ?? []).forEach((client) => {
      if (client.productId) {
        byId.set(client.productId, client.productName || client.productId);
      }
    });
    return [...byId.entries()].map(([value, label]) => ({ value, label }));
  }, [overview.data?.clientsWithoutChannel]);

  const clientsWithoutChannel = useMemo(() => {
    const phrase = search.trim().toLowerCase();
    return (overview.data?.clientsWithoutChannel ?? []).filter((client) => {
      if (productFilter && client.productId !== productFilter) {
        return false;
      }
      if (!phrase) {
        return true;
      }
      return `${client.firstName ?? ""} ${client.lastName ?? ""} ${client.email}`.toLowerCase().includes(phrase);
    });
  }, [overview.data?.clientsWithoutChannel, search, productFilter]);

  const isFiltered = Boolean(productFilter || search.trim());

  const onConfigSubmit = async (model: ConfigFormModel) => {
    try {
      const result = await updateConfig.mutateAsync({
        enabled: model.enabled,
        channelName: model.channelName.trim() || DEFAULT_ANNOUNCEMENT_CHANNEL_NAME,
        channelType: model.channelType,
        applyToExisting: model.applyToExisting ?? false,
      });
      void message.success(
        result.updatedChannels > 0
          ? `Zapisano konfigurację i zaktualizowano ${result.updatedChannels} istniejących kanałów`
          : "Zapisano konfigurację kanału ogłoszeniowego",
      );
    } catch {
      void message.error("Nie udało się zapisać konfiguracji");
    }
  };

  const runBackfill = async (traineeIds?: string[]) => {
    try {
      const result = await backfill.mutateAsync(traineeIds);
      if (result.failed.length) {
        void message.warning(`Utworzono ${result.created} z ${result.processed} kanałów — ${result.failed.length} błędów`);
      } else {
        void message.success(
          result.processed === 1 ? "Kanał ogłoszeniowy utworzony" : `Utworzono ${result.created} kanałów ogłoszeniowych`,
        );
      }
    } catch {
      void message.error("Nie udało się utworzyć kanałów. Spróbuj ponownie.");
    } finally {
      setPendingTraineeId(null);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      footer={null}
      title={
        <Space>
          <NotificationOutlined />
          Kanał ogłoszeniowy
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" className="mt-2">
        Dodatkowy kanał czatowy na ogłoszenia, promocje i wskazówki. Nowe klientki dostają go automatycznie przy
        aktywacji konta; istniejącym możesz utworzyć go poniżej.
      </Typography.Paragraph>

      {!isOwner && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Konfigurację kanału może zmieniać tylko właściciel konta."
        />
      )}

      {overview.isError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Nie udało się pobrać danych kanału ogłoszeniowego. Odśwież i spróbuj ponownie."
        />
      )}

      <Form<ConfigFormModel>
        layout="vertical"
        form={formController}
        onFinish={onConfigSubmit}
        disabled={!isOwner || overview.isLoading}
      >
        <Form.Item name="enabled" label="Kanał ogłoszeniowy włączony" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          name="channelName"
          label="Nazwa kanału"
          tooltip="Tak kanał będzie wyświetlany klientkom na liście czatów"
        >
          <Input placeholder={DEFAULT_ANNOUNCEMENT_CHANNEL_NAME} maxLength={80} />
        </Form.Item>

        <Form.Item name="channelType" label="Typ kanału">
          <Radio.Group>
            <Radio value={ChatType.OneWayMarketing}>Jednokierunkowy — klientki tylko czytają</Radio>
            <Radio value={ChatType.TwoWayMarketing}>Dwukierunkowy — klientki mogą odpisywać</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="applyToExisting"
          label="Zastosuj nazwę i typ także do istniejących kanałów"
          valuePropName="checked"
          initialValue={false}
          tooltip="Bez tego zmiana obejmie tylko kanały tworzone od teraz. Nadpisze też kanały ustawione inaczej przez automatyzacje (do czasu kolejnej aktywacji klientki)."
        >
          <Switch />
        </Form.Item>

        <Form.Item className="mb-2">
          <Button type="primary" htmlType="submit" loading={updateConfig.isLoading}>
            Zapisz konfigurację
          </Button>
        </Form.Item>
      </Form>

      <Divider />

      <div className="mb-4 flex gap-8">
        <Statistic title="Aktywne klientki" value={overview.data?.totalActive ?? "—"} />
        <Statistic title="Z kanałem" value={overview.data?.withChannel ?? "—"} />
        <Statistic title="Bez kanału" value={overview.data?.clientsWithoutChannel.length ?? "—"} />
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Space wrap>
          <Input.Search
            placeholder="Wyszukaj klientkę..."
            allowClear
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ width: 220 }}
          />
          <Select
            placeholder="Produkt"
            allowClear
            value={productFilter}
            onChange={(value) => setProductFilter(value ?? null)}
            options={productOptions}
            // Fixed width — a selected long product name must not resize the row and push
            // the inputs apart.
            style={{ width: 220 }}
          />
        </Space>
        <Popconfirm
          title={`Utworzyć kanał ogłoszeniowy dla ${clientsWithoutChannel.length} klientek?`}
          okText="Utwórz"
          cancelText="Anuluj"
          disabled={!canBackfill || !clientsWithoutChannel.length}
          onConfirm={() => runBackfill(isFiltered ? clientsWithoutChannel.map((client) => client.traineeId) : undefined)}
        >
          <Tooltip title={backfillDisabledHint}>
            <Button
              type="primary"
              ghost
              disabled={!canBackfill || !clientsWithoutChannel.length}
              loading={backfill.isLoading && pendingTraineeId === null}
            >
              {isFiltered
                ? `Utwórz dla przefiltrowanych (${clientsWithoutChannel.length})`
                : `Utwórz dla wszystkich (${clientsWithoutChannel.length})`}
            </Button>
          </Tooltip>
        </Popconfirm>
      </div>

      <Table<ClientWithoutChannel>
        rowKey="traineeId"
        size="small"
        loading={overview.isLoading}
        dataSource={clientsWithoutChannel}
        pagination={{ defaultPageSize: 10, showSizeChanger: false }}
        columns={[
          {
            title: "Klientka",
            key: "name",
            render: (_, client) => (
              <div>
                <div>{`${client.firstName ?? ""} ${client.lastName ?? ""}`.trim() || "—"}</div>
                <Typography.Text type="secondary" className="text-xs">
                  {client.email}
                </Typography.Text>
              </div>
            ),
          },
          {
            title: "Produkt",
            key: "product",
            width: 180,
            render: (_, client) => (
              <Typography.Text type="secondary" className="text-xs">
                {client.productName || "—"}
              </Typography.Text>
            ),
          },
          {
            title: "",
            key: "actions",
            width: 140,
            render: (_, client) => (
              <Tooltip title={backfillDisabledHint}>
                <Button
                  size="small"
                  disabled={!canBackfill}
                  loading={backfill.isLoading && pendingTraineeId === client.traineeId}
                  onClick={() => {
                    setPendingTraineeId(client.traineeId);
                    void runBackfill([client.traineeId]);
                  }}
                >
                  Utwórz kanał
                </Button>
              </Tooltip>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default AnnouncementChannelModal;
